import { motion } from 'framer-motion';
import { useForm, ValidationError } from '@formspree/react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function ContactForm() {
  const [state, handleSubmit] = useForm("mnnvbzke");
  
  if (state.succeeded) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-effect p-6 md:p-8 rounded-2xl border border-white/5 hover:border-primary/20 transition-all duration-300 hover:shadow-xl"
      >
        <h3 className="font-orbitron font-semibold text-2xl mb-4">Thank You!</h3>
        <p className="text-foreground/90">Your message has been sent successfully. We'll get back to you soon!</p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-effect p-6 md:p-8 rounded-2xl border border-white/5 hover:border-primary/20 transition-all duration-300 hover:shadow-xl relative bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-lg"
    >
      <div className="mb-6">
        <h3 className="font-orbitron font-semibold text-2xl md:text-3xl mb-2 bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/80">
          Get In Touch
        </h3>
        <p className="text-foreground/80 text-sm">Have a project in mind? Let's make it happen!</p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="firstName" className="block text-sm font-semibold mb-2 text-white">
              First Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="firstName"
              name="firstName"
              className="w-full bg-white/5 border border-white/10 focus:border-primary/50 text-foreground placeholder:text-foreground/50 transition-colors"
              placeholder="John"
              required
            />
            <ValidationError 
              prefix="First Name" 
              field="firstName"
              errors={state.errors}
              className="text-red-500 text-xs mt-1"
            />
          </div>
          <div>
            <Label htmlFor="lastName" className="block text-sm font-semibold mb-2 text-white">
              Last Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="lastName"
              name="lastName"
              className="w-full bg-white/5 border border-white/10 focus:border-primary/50 text-foreground placeholder:text-foreground/50 transition-colors"
              placeholder="Doe"
              required
            />
            <ValidationError 
              prefix="Last Name" 
              field="lastName"
              errors={state.errors}
              className="text-red-500 text-xs mt-1"
            />
          </div>
        </div>
        
        <div>
          <Label htmlFor="email" className="block text-sm font-semibold mb-2 text-white">
            Email <span className="text-red-500">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            name="email"
            className="w-full bg-white/5 border border-white/10 focus:border-primary/50 text-foreground placeholder:text-foreground/50 transition-colors"
            placeholder="your@email.com"
            required
          />
          <ValidationError 
            prefix="Email" 
            field="email"
            errors={state.errors}
            className="text-red-500 text-xs mt-1"
          />
        </div>
        
        <div>
          <Label htmlFor="subject" className="block text-sm font-semibold mb-2 text-white">
            Subject
          </Label>
          <Select name="subject">
            <SelectTrigger className="w-full bg-background/50 border-border/50 focus:border-primary/50">
              <SelectValue placeholder="Select a subject" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="General Inquiry">General Inquiry</SelectItem>
              <SelectItem value="Project Quote">Project Quote</SelectItem>
              <SelectItem value="Support">Support</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
          <input type="hidden" name="_subject" value="New Contact Form Submission" />
        </div>
        
        <div>
          <Label htmlFor="message" className="block text-sm font-semibold mb-2 text-white">
            Message <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="message"
            name="message"
            className="w-full bg-white/5 border border-white/10 focus:border-primary/50 text-foreground placeholder:text-foreground/50 min-h-[140px] transition-colors"
            placeholder="Tell us about your project, timeline, and any specific requirements..."
            required
          />
          <ValidationError 
            prefix="Message" 
            field="message"
            errors={state.errors}
            className="text-red-500 text-xs mt-1"
          />
        </div>
        
        <div className="pt-2">
          <Button
            type="submit"
            className="w-full md:w-auto px-8 py-6 text-base font-semibold bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 group"
            disabled={state.submitting}
          >
            <span className="relative z-10 flex items-center gap-2">
              {state.submitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </>
              ) : (
                <>
                  <span>Send Message</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1 transition-transform duration-300 group-hover:translate-x-1">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </>
              )}
            </span>
          </Button>
          <p className="text-xs md:text-sm text-foreground/70 mt-4 text-center leading-tight">
            We typically respond within 24-48 hours. For urgent inquiries, call 
            <a href="tel:+447743307781" className="text-primary hover:underline whitespace-nowrap font-medium">+44 7743 307781</a>
          </p>
        </div>
      </form>
      
      <div className="absolute -bottom-8 -right-8 w-40 h-40 opacity-10">
        <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <path
            fill="currentColor"
            d="M45,-76.4C58.5,-69.2,70,-58.1,77.6,-44.3C85.2,-30.6,88.9,-14.3,88.5,0.1C88.2,14.6,83.9,29.2,75.3,40.9C66.7,52.6,53.9,61.4,40.2,70.2C26.5,79,11.9,87.8,-2.4,91.4C-16.7,95.1,-33.4,93.6,-44.6,84.5C-55.8,75.4,-61.5,58.7,-69.9,42.6C-78.3,26.6,-89.5,11.3,-90.8,-5.3C-92.1,-22,-83.5,-44.1,-69.2,-51.9C-54.9,-59.8,-34.9,-53.5,-21.3,-61.9C-7.7,-70.3,-0.6,-93.5,10.5,-99.9C21.6,-106.4,31.5,-83.6,45,-76.4Z"
            transform="translate(100 100)"
          />
        </svg>
      </div>
    </motion.div>
  )
}
